import * as React from 'react';
import Emoji from 'react-emoji-render';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { ReactComponent as HexLogo } from '../../images/hex-logo.svg';
import {
  ANIMATIONS,
  DOCS_LINKS,
  INTERNAL_LINKS,
  MEDIA_KIT_LINK,
  TRUST_LINKS,
  TWITTER_LINK
} from '../../utils';
import { gridColorSelector } from '../v2/GridLines';

import { HitCounter } from './HitCounter';
import { LinkWithQuery } from './LinkWithQuery';
import { Rotator } from './Rotator';

const madeWithEmojis = [
  <Emoji text=":doughnut:" />,
  <Emoji text=":coffee:" />,
  <Emoji text=":dumpling:" />,
  <Emoji text=":beer:" />,
  <Emoji text=":cake:" />,
  <Emoji text=":crystal_ball:" />,
  <Emoji text=":lock:" />,
  <Emoji text=":baguette_bread:" />,
  <Emoji text=":wine_glass:" />,
  <Emoji text=":sleeping_bed:" />,
  <Emoji text=":purple_heart:" />,
  <Emoji text=":pretzel:" />,
  <Emoji text=":skateboard:" />,
  <Emoji text=":fried_shrimp:" />,
  <Emoji text=":garlic:" />,
  <Emoji text=":bread:" />,
  <Emoji text=":coconut:" />,
  <Emoji text=":golf:" />,
  <Emoji text=":crossed_fingers:" />
];

const currentYear = new Date().getFullYear();

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  width: 100%;

  [data-theme='pink'] & {
    background: ${({ theme }) => theme.paletteCssVars.bg400};
  }
`;

const Inner = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0;
  padding: 50px 30px;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  width: 100%;
  margin: 0 auto;

  a {
    border-bottom: none;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: flex;
    flex-direction: column;
    gap: 45px;
  }
`;

const Divider = styled.div<{ $v2Colors?: boolean }>`
  height: 1px;
  flex: 1 1 auto;
  background: ${({ theme, $v2Colors }) =>
    $v2Colors ? gridColorSelector(theme, 'major') : theme.footer.divider.color};
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;

  svg {
    height: 20px;
    width: auto;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    align-items: center;
  }
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 60px;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    align-items: center;
  }
`;

const MadeWith = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.fontColor.MUTED};

  > span {
    &:first-child {
      margin-right: 4px;
    }

    &:last-child {
      margin-left: 4px;
    }
  }

  [data-theme='pink'] & {
    color: ${({ theme }) => theme.paletteCssVars.fg1100};
  }
`;

const Links = styled.div`
  display: flex;
  gap: 60px;
  width: 65%;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    width: auto;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    justify-content: center;
  }
`;

const Bottom = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  a {
    color: ${({ theme }) => theme.fontColor.MUTED};
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    flex-direction: column;
    gap: 10px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  flex: 1 1 0;

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    align-items: center;
  }
`;

const StyledLinkWithQuery = styled(LinkWithQuery)`
  [data-theme='pink'] & {
    color: ${({ theme }) => theme.paletteCssVars.fg1100};
  }
`;

const A = styled.a`
  [data-theme='pink'] & {
    color: ${({ theme }) => theme.paletteCssVars.fg1100};
  }
`;

const ColTitle = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  font-weight: ${({ theme }) => theme.fontWeight.BOLD};
  color: ${({ theme }) => theme.fontColor.LOUD};
  text-transform: uppercase;
  letter-spacing: 0.7px;

  [data-theme='pink'] & {
    color: ${({ theme }) => theme.paletteCssVars.fg1200};
  }
`;

const Copyright = styled.div`
  color: ${({ theme }) => theme.fontColor.MUTED};

  [data-theme='pink'] & {
    color: ${({ theme }) => theme.paletteCssVars.fg1200};
  }
`;

export const Footer: React.ComponentType<{ v2Colors?: boolean }> = React.memo(
  props => {
    return (
      <Wrapper {...ANIMATIONS.SimpleFadeIn}>
        <Divider $v2Colors={props.v2Colors} />
        <Inner>
          <Left>
            <HexLogo />
            <MadeWith>
              <span>Made with</span>
              <Rotator
                itemHeight={20}
                itemWidth={20}
                noFade={true}
                options={madeWithEmojis}
              />
              <span>
                on <Emoji text=":earth_americas:" />.
              </span>
            </MadeWith>
          </Left>
          <Center>
            <Links>
              <Column>
                <ColTitle>Company</ColTitle>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Careers}>
                  Careers
                </StyledLinkWithQuery>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Customers}>
                  Customers
                </StyledLinkWithQuery>
                <StyledLinkWithQuery isExternal={true} to={MEDIA_KIT_LINK}>
                  Media kit
                </StyledLinkWithQuery>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Newsroom}>
                  Newsroom
                </StyledLinkWithQuery>
              </Column>
              <Column>
                <ColTitle>Product</ColTitle>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Notebooks}>
                  Notebooks
                </StyledLinkWithQuery>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Explore}>
                  Explore
                </StyledLinkWithQuery>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Magic}>
                  Hex Magic
                </StyledLinkWithQuery>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Enterprise}>
                  Enterprise
                </StyledLinkWithQuery>
                <StyledLinkWithQuery to={INTERNAL_LINKS.ForTeams}>
                  For Teams
                </StyledLinkWithQuery>
                <StyledLinkWithQuery to={INTERNAL_LINKS.AppBuilder}>
                  App Builder
                </StyledLinkWithQuery>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Pricing}>
                  Pricing
                </StyledLinkWithQuery>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Integrations}>
                  Integrations
                </StyledLinkWithQuery>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Security}>
                  Security
                </StyledLinkWithQuery>
              </Column>
              <Column>
                <ColTitle>Resources</ColTitle>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Blog}>
                  Blog
                </StyledLinkWithQuery>
                <StyledLinkWithQuery isExternal={true} to={DOCS_LINKS.Home}>
                  Docs
                </StyledLinkWithQuery>
                <StyledLinkWithQuery
                  isExternal={true}
                  to={DOCS_LINKS.Quickstart}>
                  Quickstart
                </StyledLinkWithQuery>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Resources}>
                  Resources
                </StyledLinkWithQuery>
                <StyledLinkWithQuery to={INTERNAL_LINKS.Templates}>
                  Templates
                </StyledLinkWithQuery>
                <StyledLinkWithQuery
                  isExternal={true}
                  to={DOCS_LINKS.Changelog}>
                  Changelog
                </StyledLinkWithQuery>
                <StyledLinkWithQuery isExternal={true} to={TRUST_LINKS.Home}>
                  Trust Center
                </StyledLinkWithQuery>
              </Column>
              <Column>
                <ColTitle>Contact</ColTitle>
                <A
                  href="mailto:support@hex.tech"
                  rel="noreferrer"
                  target="_blank">
                  Support
                </A>
                <StyledLinkWithQuery to={INTERNAL_LINKS.ContactUs}>
                  Sales
                </StyledLinkWithQuery>
                <StyledLinkWithQuery isExternal={true} to={TWITTER_LINK}>
                  Twitter
                </StyledLinkWithQuery>
                <StyledLinkWithQuery
                  isExternal={true}
                  to="mailto:hello@hex.tech">
                  Email us
                </StyledLinkWithQuery>
              </Column>
            </Links>
            <Bottom>
              <Copyright>© {currentYear} Hex Technologies Inc.</Copyright>
              <StyledLinkWithQuery
                isExternal={true}
                to={DOCS_LINKS.PrivacyPolicy}>
                Privacy policy
              </StyledLinkWithQuery>
              <StyledLinkWithQuery
                isExternal={true}
                to={DOCS_LINKS.TermsAndConditions}>
                Terms &amp; conditions
              </StyledLinkWithQuery>
              <StyledLinkWithQuery
                isExternal={true}
                to={DOCS_LINKS.ModernSlaveryStatement}>
                Modern slavery statement
              </StyledLinkWithQuery>
            </Bottom>
          </Center>
          <Right>
            <HitCounter />
          </Right>
        </Inner>
        {props.v2Colors ? <Divider $v2Colors={props.v2Colors} /> : null}
      </Wrapper>
    );
  }
);
